import { useEffect, useMemo, useState } from 'react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { fetchRaster } from '../api/calculation-positions.api'
import type { CalcRaster } from '../types/calculation.types'
import { Kennzeile, geordnet } from './PositionsListe'
import { ZUSTAND_ANZEIGE } from './PositionsZeile'

/**
 * AP-37 Nachtrag 1 (N1-12) — **Positionen in der Übersicht**, wie die Karte
 * „Projektpositionen" im Projekt: je Zeile Nr./OZ, Kennzeile, EP, GP und
 * Zustand, nach Gruppen; ein Klick öffnet die Position.
 */
export function PositionenKarte({ kalkulationId, stand, onOeffnen }: { kalkulationId: string; stand: string; onOeffnen: (id: string) => void }) {
  const [raster, setRaster] = useState<CalcRaster | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    fetchRaster(kalkulationId)
      .then((r) => {
        setRaster(r)
        setFehler(null)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Positionen ließen sich nicht laden.')))
  }, [kalkulationId, stand])

  const bloecke = useMemo(() => {
    if (!raster) return []
    return [
      { id: null as string | null, name: '', nummer: '', ebene: 0, positionen: raster.positions.filter((p) => !p.groupId) },
      ...geordnet(raster.groups).map((g) => ({ id: g.id as string | null, name: g.name, nummer: g.displayNumber, ebene: g.level, positionen: raster.positions.filter((p) => p.groupId === g.id) })),
    ].filter((b) => b.positionen.length > 0)
  }, [raster])

  const anzahl = raster?.positions.filter((p) => p.kind !== 'TEXT').length ?? 0

  return (
    <section className="flex flex-col rounded-lg border border-border bg-card p-4">
      <h3 className="mb-2 text-sm font-semibold">
        Positionen {raster && <span className="font-normal text-muted-foreground">· {anzahl}</span>}
      </h3>
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {!raster && !fehler && <p className="text-sm text-muted-foreground">Lädt …</p>}
      {raster && anzahl === 0 && <p className="text-sm text-muted-foreground">Noch keine Positionen.</p>}
      {raster && anzahl > 0 && (
        <div className="max-h-[28rem] overflow-auto">
          <table className="w-full min-w-[30rem] table-fixed text-sm">
            <colgroup>
              <col className="w-20" />
              <col />
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-28" />
            </colgroup>
            <thead className="sticky top-0 bg-card">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 font-normal">Nr./OZ</th>
                <th className="py-1 font-normal">Position</th>
                <th className="py-1 text-right font-normal">EP</th>
                <th className="py-1 text-right font-normal">GP</th>
                <th className="py-1 pl-3 font-normal">Zustand</th>
              </tr>
            </thead>
            {bloecke.map((b) => (
              <tbody key={b.id ?? 'ohne'}>
                {b.id && (
                  <tr className="border-t border-border/60">
                    <td colSpan={5} className="truncate pb-0.5 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ paddingLeft: `${b.ebene * 0.5}rem` }}>
                      {b.nummer && <span className="font-mono normal-case">{b.nummer} </span>}
                      {b.name}
                    </td>
                  </tr>
                )}
                {b.positionen.map((p) => {
                  const z = ZUSTAND_ANZEIGE[p.state]
                  const nebensache = p.kind === 'OPTIONAL' || p.kind === 'ALTERNATIVE'
                  return (
                    <tr key={p.id} className={`cursor-pointer border-t border-border/60 hover:bg-accent/50 ${nebensache ? 'opacity-70' : ''}`} onClick={() => onOeffnen(p.id)}>
                      <td className="truncate py-1 font-mono text-xs text-muted-foreground">{p.displayNumber}</td>
                      <td className="truncate py-1" title={p.shortText}>
                        <Kennzeile p={p} />
                        {p.kind === 'OPTIONAL' && <span className="ml-1 text-[11px] text-muted-foreground">Eventual</span>}
                        {p.kind === 'ALTERNATIVE' && <span className="ml-1 text-[11px] text-muted-foreground">Wahl</span>}
                      </td>
                      <td className="py-1 text-right tabular-nums">{p.kind === 'TEXT' ? '' : geldText(p.unitPrice)}</td>
                      <td className="py-1 text-right tabular-nums">{p.kind === 'TEXT' ? '' : geldText(p.totalPrice)}</td>
                      <td className="py-1 pl-3">{p.kind !== 'TEXT' && <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] ${z.klasse}`}>{z.label}</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  )
}
